import React, { useState, useEffect } from 'react';
import { RefreshCw, Mail, Phone, Clock, User, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchInquiries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/contact/inquiries`);
      if (!response.ok) throw new Error('Fehler beim Laden');
      const data = await response.json();
      // Sort by date, newest first
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setInquiries(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSubjectLabel = (subject) => {
    const labels = {
      sanierung: 'Sanierung/Ausbau',
      projektentwicklung: 'Projektentwicklung',
      verkauf: 'Immobilie verkaufen',
      partnerschaft: 'Partnerschaft/Investition',
      monteurvermietung: 'Monteurvermietung',
      sonstiges: 'Sonstiges'
    };
    return labels[subject] || subject;
  };

  const getSubjectColor = (subject) => {
    const colors = {
      sanierung: 'bg-green-100 text-green-800',
      projektentwicklung: 'bg-blue-100 text-blue-800',
      verkauf: 'bg-amber-100 text-amber-800',
      partnerschaft: 'bg-purple-100 text-purple-800',
      monteurvermietung: 'bg-indigo-100 text-indigo-800',
      sonstiges: 'bg-gray-100 text-gray-800'
    };
    return colors[subject] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Header */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Kontaktanfragen
              </h1>
              <p className="text-gray-600">
                Übersicht aller eingegangenen Anfragen
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <span className="text-sm text-gray-500">
                {inquiries.length} Anfragen
              </span>
              <Button 
                onClick={fetchInquiries} 
                variant="outline"
                disabled={loading}
                className="flex items-center"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {error && (
            <Card className="mb-6 border-red-200 bg-red-50">
              <CardContent className="p-4 flex items-center text-red-700">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : inquiries.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Keine Anfragen vorhanden
                </h3>
                <p className="text-gray-500">
                  Neue Anfragen werden hier angezeigt, sobald sie eingehen.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {inquiries.map((inquiry) => (
                <Card key={inquiry.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                      {/* Left side - Main info */}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSubjectColor(inquiry.subject)}`}>
                            {getSubjectLabel(inquiry.subject)}
                          </span>
                          {inquiry.email_sent ? (
                            <span className="flex items-center text-xs text-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              E-Mail versendet
                            </span>
                          ) : (
                            <span className="flex items-center text-xs text-amber-600">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              In Datenbank gespeichert
                            </span>
                          )}
                        </div>

                        <div className="flex items-center mb-2">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="font-semibold text-gray-900">{inquiry.name}</span>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                          <a href={`mailto:${inquiry.email}`} className="flex items-center hover:text-blue-600">
                            <Mail className="w-4 h-4 mr-1" />
                            {inquiry.email}
                          </a>
                          {inquiry.phone && (
                            <a href={`tel:${inquiry.phone}`} className="flex items-center hover:text-blue-600">
                              <Phone className="w-4 h-4 mr-1" />
                              {inquiry.phone}
                            </a>
                          )}
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-gray-700 whitespace-pre-wrap">{inquiry.message}</p>
                        </div>
                      </div>

                      {/* Right side - Meta info */}
                      <div className="mt-4 lg:mt-0 lg:ml-6 lg:text-right lg:min-w-[200px]">
                        <div className="flex items-center lg:justify-end text-sm text-gray-500 mb-2">
                          <Clock className="w-4 h-4 mr-1" />
                          {formatDate(inquiry.created_at)}
                        </div>
                        <div className="text-xs text-gray-400 mb-3">
                          ID: {inquiry.id.slice(0, 8)}...
                        </div>
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">Weitergeleitet an:</span><br />
                          <a href={`mailto:${inquiry.recipient_email}`} className="text-blue-600 hover:underline">
                            {inquiry.recipient_email}
                          </a>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminInquiries;
